// src/admin/AdminOrders.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";

const formatPrice = (value) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatDate = (timestamp) => {
  if (!timestamp) return "—";

  try {
    const date =
      typeof timestamp.toDate === "function"
        ? timestamp.toDate()
        : new Date(timestamp);

    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(date);
  } catch {
    return "—";
  }
};

const getStatusBadge = (status) => {
  switch (status) {
    case "paid":
      return "bg-success";

    case "confirmed":
      return "bg-primary";

    case "processing":
      return "bg-info text-dark";

    case "completed":
      return "bg-dark";

    case "cancelled":
      return "bg-danger";

    case "failed":
      return "bg-danger";

    case "pending":
    default:
      return "bg-warning text-dark";
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case "paid":
      return "Payée";

    case "confirmed":
      return "Confirmée";

    case "processing":
      return "En traitement";

    case "completed":
      return "Terminée";

    case "cancelled":
      return "Annulée";

    case "failed":
      return "Échec paiement";

    case "pending":
    default:
      return "En attente";
  }
};

/* =========================================================
   TOAST
========================================================= */

function OrderToast({ toast, onClose }) {
  return (
    <div
      className="bg-white border shadow-lg rounded-4 overflow-hidden"
      style={{
        width: "min(380px, calc(100vw - 30px))",
        animation: "orderToastIn 0.25s ease-out",
      }}
    >
      <div className="d-flex">
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            width: 55,
            background: "#0d6efd",
            color: "#fff",
            fontSize: 22,
          }}
        >
          🛒
        </div>

        <div className="flex-grow-1 p-3">
          <div className="d-flex justify-content-between align-items-start gap-2">
            <div>
              <div className="fw-bold">Nouvelle commande</div>

              <div className="small text-muted">
                {toast.identifier || "Nouvelle commande reçue"}
              </div>
            </div>

            <button
              type="button"
              className="btn-close"
              aria-label="Fermer"
              onClick={() => onClose(toast.id)}
            />
          </div>

          <div className="mt-2">
            {toast.customerName && (
              <div className="small">
                <strong>Client :</strong> {toast.customerName}
              </div>
            )}

            <div className="fw-bold text-primary mt-1">
              {formatPrice(toast.amount)} CFA
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   PAGE
========================================================= */

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [selectedOrder, setSelectedOrder] = useState(null);

  const [toasts, setToasts] = useState([]);

  /*
   * Permet d'éviter d'afficher un toast pour toutes
   * les commandes déjà présentes lors du premier chargement.
   */
  const initializedRef = useRef(false);

  /*
   * Ensemble des IDs déjà connus.
   */
  const knownOrderIdsRef = useRef(new Set());

  const toastTimersRef = useRef([]);

  /* =========================================================
     TOAST
  ========================================================= */

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const showNewOrderToast = (order) => {
    const toastId = `${order.id}-${Date.now()}`;

    const toast = {
      id: toastId,
      identifier: order.identifier,
      customerName: order.customer?.fullName || "",
      amount: order.amount || 0,
    };

    setToasts((prev) => [toast, ...prev].slice(0, 4));

    const timer = window.setTimeout(() => {
      removeToast(toastId);
    }, 6000);

    toastTimersRef.current.push(timer);
  };

  /* =========================================================
     FIRESTORE TEMPS RÉEL
  ========================================================= */

  useEffect(() => {
    const ordersQuery = query(
      collection(db, "orders_prosoft"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      ordersQuery,

      (snapshot) => {
        const list = snapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        }));

        /*
         * PREMIER CHARGEMENT
         *
         * On remplit les IDs connus mais on ne génère
         * aucun toast pour les anciennes commandes.
         */
        if (!initializedRef.current) {
          knownOrderIdsRef.current = new Set(
            snapshot.docs.map((document) => document.id)
          );

          initializedRef.current = true;

          setOrders(list);
          setLoading(false);

          return;
        }

        /*
         * CHANGEMENTS APRÈS LE PREMIER CHARGEMENT
         */
        snapshot.docChanges().forEach((change) => {
          if (change.type !== "added") return;

          const id = change.doc.id;

          /*
           * Protection contre les doublons.
           */
          if (knownOrderIdsRef.current.has(id)) {
            return;
          }

          const newOrder = {
            id,
            ...change.doc.data(),
          };

          knownOrderIdsRef.current.add(id);

          showNewOrderToast(newOrder);
        });

        /*
         * Synchronise les IDs connus.
         */
        snapshot.docs.forEach((document) => {
          knownOrderIdsRef.current.add(document.id);
        });

        /*
         * ✅ La page est automatiquement rafraîchie ici.
         */
        setOrders(list);
        setLoading(false);
      },

      (error) => {
        console.error(
          "Erreur chargement temps réel des commandes :",
          error
        );

        setLoading(false);
      }
    );

    return () => {
      unsubscribe();

      toastTimersRef.current.forEach((timer) => {
        clearTimeout(timer);
      });
    };
  }, []);

  /* =========================================================
     FILTRAGE
  ========================================================= */

  const filteredOrders = useMemo(() => {
    let result = [...orders];

    if (filter !== "all") {
      result = result.filter(
        (order) => (order.status || "pending") === filter
      );
    }

    const term = search.trim().toLowerCase();

    if (term) {
      result = result.filter((order) => {
        const values = [
          order.identifier,
          order.customer?.fullName,
          order.customer?.phone,
          order.customer?.email,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return values.includes(term);
      });
    }

    return result;
  }, [orders, filter, search]);

  /* =========================================================
     STATS
  ========================================================= */

  const stats = useMemo(() => {
    const total = orders.length;

    const pending = orders.filter(
      (order) => (order.status || "pending") === "pending"
    ).length;

    const paid = orders.filter(
      (order) => order.status === "paid"
    ).length;

    const amount = orders
      .filter(
        (order) =>
          order.status === "paid" ||
          order.status === "completed"
      )
      .reduce(
        (sum, order) => sum + (Number(order.amount) || 0),
        0
      );

    return {
      total,
      pending,
      paid,
      amount,
    };
  }, [orders]);

  /* =========================================================
     MODIFIER STATUT
  ========================================================= */

  const handleStatusChange = async (order, newStatus) => {
    try {
      await updateDoc(
        doc(db, "orders_prosoft", order.id),
        {
          status: newStatus,
          updatedAt: serverTimestamp(),
        }
      );

      /*
       * Aucun setOrders manuel.
       *
       * onSnapshot() recevra la modification
       * et actualisera automatiquement le tableau.
       */
    } catch (error) {
      console.error(
        "Erreur modification statut commande :",
        error
      );

      alert(
        "Impossible de modifier le statut de cette commande."
      );
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes orderToastIn {
            from {
              opacity: 0;
              transform: translateX(30px);
            }

            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          .admin-order-card {
            transition: transform .18s ease, box-shadow .18s ease;
          }

          .admin-order-card:hover {
            transform: translateY(-2px);
          }

          .order-product-image {
            width: 42px;
            height: 42px;
            object-fit: cover;
            border-radius: 8px;
            background: #f5f5f5;
          }
        `}
      </style>

      {/* =====================================================
          TOASTS
      ====================================================== */}

      <div
        className="position-fixed d-flex flex-column gap-2"
        style={{
          top: 20,
          right: 20,
          zIndex: 99999,
        }}
      >
        {toasts.map((toast) => (
          <OrderToast
            key={toast.id}
            toast={toast}
            onClose={removeToast}
          />
        ))}
      </div>

      {/* =====================================================
          PAGE
      ====================================================== */}

      <div className="container py-4">
        {/* TITRE */}

        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
          <div>
            <h4 className="fw-bold mb-1">
              Commandes
            </h4>

            <div className="small text-muted">
              Suivi des commandes reçues en temps réel
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span
              className="badge rounded-pill bg-success-subtle text-success border border-success-subtle"
              style={{
                padding: "8px 12px",
              }}
            >
              ● Temps réel actif
            </span>
          </div>
        </div>

        {/* =====================================================
            STATS
        ====================================================== */}

        <div className="row g-3 mb-4">
          <div className="col-6 col-lg-3">
            <div className="card h-100 border-0 shadow-sm rounded-4">
              <div className="card-body">
                <div className="small text-muted mb-1">
                  Commandes
                </div>

                <div className="fs-4 fw-bold">
                  {stats.total}
                </div>
              </div>
            </div>
          </div>

          <div className="col-6 col-lg-3">
            <div className="card h-100 border-0 shadow-sm rounded-4">
              <div className="card-body">
                <div className="small text-muted mb-1">
                  En attente
                </div>

                <div className="fs-4 fw-bold text-warning">
                  {stats.pending}
                </div>
              </div>
            </div>
          </div>

          <div className="col-6 col-lg-3">
            <div className="card h-100 border-0 shadow-sm rounded-4">
              <div className="card-body">
                <div className="small text-muted mb-1">
                  Payées
                </div>

                <div className="fs-4 fw-bold text-success">
                  {stats.paid}
                </div>
              </div>
            </div>
          </div>

          <div className="col-6 col-lg-3">
            <div className="card h-100 border-0 shadow-sm rounded-4">
              <div className="card-body">
                <div className="small text-muted mb-1">
                  Montant encaissé
                </div>

                <div className="fs-5 fw-bold text-primary">
                  {formatPrice(stats.amount)} CFA
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            FILTRES
        ====================================================== */}

        <div className="card border-0 shadow-sm rounded-4 mb-3">
          <div className="card-body">
            <div className="row g-2">
              <div className="col-md-7">
                <input
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Rechercher par commande, client, téléphone..."
                  value={search}
                  onChange={(e) =>
                    setSearch(e.target.value)
                  }
                />
              </div>

              <div className="col-md-5">
                <select
                  className="form-select form-select-sm"
                  value={filter}
                  onChange={(e) =>
                    setFilter(e.target.value)
                  }
                >
                  <option value="all">
                    Toutes les commandes
                  </option>

                  <option value="pending">
                    En attente
                  </option>

                  <option value="paid">
                    Payées
                  </option>

                  <option value="confirmed">
                    Confirmées
                  </option>

                  <option value="processing">
                    En traitement
                  </option>

                  <option value="completed">
                    Terminées
                  </option>

                  <option value="cancelled">
                    Annulées
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            LISTE
        ====================================================== */}

        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0">
                Liste des commandes
              </h6>

              {!loading && (
                <span className="badge bg-light text-dark border">
                  {filteredOrders.length}
                </span>
              )}
            </div>

            {/* SKELETON */}

            {loading && (
              <div>
                {[1, 2, 3, 4, 5].map((item) => (
                  <div
                    key={item}
                    className="border-bottom py-3 placeholder-glow"
                  >
                    <div className="row align-items-center">
                      <div className="col-3">
                        <span className="placeholder col-8" />
                      </div>

                      <div className="col-3">
                        <span className="placeholder col-10" />
                      </div>

                      <div className="col-2">
                        <span className="placeholder col-7" />
                      </div>

                      <div className="col-2">
                        <span className="placeholder col-8" />
                      </div>

                      <div className="col-2">
                        <span className="placeholder col-8" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading &&
              filteredOrders.length === 0 && (
                <div className="text-center py-5 text-muted">
                  <div style={{ fontSize: 40 }}>
                    📦
                  </div>

                  <div className="fw-semibold mt-2">
                    Aucune commande
                  </div>

                  <div className="small">
                    Les nouvelles commandes apparaîtront
                    automatiquement ici.
                  </div>
                </div>
              )}

            {!loading &&
              filteredOrders.length > 0 && (
                <div className="table-responsive">
                  <table className="table align-middle small">
                    <thead>
                      <tr>
                        <th>Commande</th>
                        <th>Client</th>
                        <th>Articles</th>
                        <th>Montant</th>
                        <th>Statut</th>
                        <th>Date</th>
                        <th></th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredOrders.map((order) => (
                        <tr key={order.id}>
                          <td>
                            <div className="fw-semibold">
                              {order.identifier || order.id}
                            </div>

                            <div className="text-muted small">
                              {order.currency || "XOF"}
                            </div>
                          </td>

                          <td>
                            <div className="fw-semibold">
                              {order.customer?.fullName ||
                                "Client"}
                            </div>

                            <div className="text-muted">
                              {order.customer?.phone || "—"}
                            </div>
                          </td>

                          <td>
                            {order.totalItems ||
                              order.items?.reduce(
                                (sum, item) =>
                                  sum +
                                  (Number(
                                    item.quantity
                                  ) || 0),
                                0
                              ) ||
                              0}
                          </td>

                          <td>
                            <div className="fw-bold">
                              {formatPrice(order.amount)} CFA
                            </div>
                          </td>

                          <td>
                            <span
                              className={`badge ${getStatusBadge(
                                order.status
                              )}`}
                            >
                              {getStatusLabel(
                                order.status
                              )}
                            </span>
                          </td>

                          <td className="text-muted text-nowrap">
                            {formatDate(order.createdAt)}
                          </td>

                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                setSelectedOrder(order)
                              }
                            >
                              Voir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* =====================================================
          MODAL / DÉTAIL COMMANDE
      ====================================================== */}

      {selectedOrder && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
          style={{
            background: "rgba(0,0,0,.5)",
            zIndex: 99990,
          }}
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="bg-white rounded-4 shadow-lg w-100"
            style={{
              maxWidth: 750,
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 p-md-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h5 className="fw-bold mb-1">
                    {selectedOrder.identifier}
                  </h5>

                  <div className="small text-muted">
                    {formatDate(
                      selectedOrder.createdAt
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() =>
                    setSelectedOrder(null)
                  }
                />
              </div>

              <hr />

              {/* CLIENT */}

              <h6 className="fw-bold">
                Client
              </h6>

              <div className="small mb-3">
                <div>
                  <strong>Nom :</strong>{" "}
                  {selectedOrder.customer?.fullName ||
                    "—"}
                </div>

                <div>
                  <strong>Téléphone :</strong>{" "}
                  {selectedOrder.customer?.phone ||
                    "—"}
                </div>

                <div>
                  <strong>Email :</strong>{" "}
                  {selectedOrder.customer?.email ||
                    "—"}
                </div>
              </div>

              {/* PRODUITS */}

              <h6 className="fw-bold">
                Produits
              </h6>

              <div className="border rounded-3 overflow-hidden mb-3">
                {(selectedOrder.items || []).map(
                  (item, index) => (
                    <div
                      key={`${item.productId}-${index}`}
                      className="d-flex align-items-center gap-3 p-2 border-bottom"
                    >
                      {item.mainImage && (
                        <img
                          src={item.mainImage}
                          alt={item.name}
                          className="order-product-image"
                        />
                      )}

                      <div className="flex-grow-1">
                        <div className="fw-semibold small">
                          {item.name}
                        </div>

                        <div className="small text-muted">
                          {formatPrice(item.price)} CFA
                          {" × "}
                          {item.quantity}
                        </div>
                      </div>

                      <div className="fw-bold small text-nowrap">
                        {formatPrice(
                          item.subtotal ||
                            (Number(item.price) || 0) *
                              (Number(
                                item.quantity
                              ) || 0)
                        )}{" "}
                        CFA
                      </div>
                    </div>
                  )
                )}
              </div>

              <div className="d-flex justify-content-between fs-5 fw-bold mb-4">
                <span>Total</span>

                <span className="text-primary">
                  {formatPrice(selectedOrder.amount)} CFA
                </span>
              </div>

              {/* STATUT */}

              <div className="border-top pt-3">
                <label className="form-label fw-semibold">
                  Statut de la commande
                </label>

                <select
                  className="form-select"
                  value={
                    selectedOrder.status || "pending"
                  }
                  onChange={async (e) => {
                    const newStatus =
                      e.target.value;

                    await handleStatusChange(
                      selectedOrder,
                      newStatus
                    );

                    setSelectedOrder((prev) => ({
                      ...prev,
                      status: newStatus,
                    }));
                  }}
                >
                  <option value="pending">
                    En attente
                  </option>

                  <option value="paid">
                    Payée
                  </option>

                  <option value="confirmed">
                    Confirmée
                  </option>

                  <option value="processing">
                    En traitement
                  </option>

                  <option value="completed">
                    Terminée
                  </option>

                  <option value="cancelled">
                    Annulée
                  </option>

                  <option value="failed">
                    Échec paiement
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
